import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { set } from '@ember/object';
import { getOwner } from '@ember/owner';
import { isTesting } from '@embroider/macros';
import '@formatjs/intl-getcanonicallocales/polyfill';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/zh';
import '@formatjs/intl-pluralrules/locale-data/ja';
import IndexModel from '../models/index';
import ENV from '../config/environment';
import { loadTimeAnalytics } from '../utils/analytics';
import '@ebiz/ember-aditools-graph/ember-aditools-graph.css';

const LOCALES = { 'en-us': 'en', 'zh-cn': 'cn', 'ja-jp': 'jp', 'ru-ru': 'ru' };

export default class IndexRoute extends Route {
  @service intl;
  @service logger;
  @service('aditools-analytics') analytics;
  @service('aditools-site') siteconfig;
  @service fastboot;
  @service headData;
  @service opamps;

  beforeModel() {
    // Ensure the presence of the terminal slash in the URL
    if (window && window.location && window.location.href) {
      let url = window.location.href;
      let queryIndex = url.indexOf('?');
      if (queryIndex === -1) {
        queryIndex = url.length;
      }
      if (url[queryIndex - 1] !== '/') {
        let newUrl = url.substring(0, queryIndex) + '/' + url.substring(queryIndex);
        window.history.replaceState({}, '', newUrl);
      }
    }
  }

  model(params) {
    const configURL = ENV.rootURL.endsWith('/') ? ENV.rootURL + 'siteconfig.json' : ENV.rootURL + '/siteconfig.json';
    const docService = getOwner(this).lookup('service:-document');
    // This little song and dance is because document isn't defined in fastboot, but docService is
    let htmlElement = docService ? docService.documentElement : document ? document.querySelector('html') : undefined;
    let fullLanguage = htmlElement?.getAttribute('lang');
    if (!fullLanguage) fullLanguage = 'en-us';
    const defaultLanguage = params.lang ? params.lang : LOCALES[fullLanguage];
    return this.siteconfig
      .start(configURL, ENV.rootURL, defaultLanguage, this.intl.locales.concat('ru-ru'), this.logger, this.fastboot)
      .then((/*site*/) => {
        if (this.siteconfig.FullLocale === 'ru-ru') {
          this.intl.setLocale('en-us');
        } else {
          this.intl.setLocale(this.siteconfig.FullLocale);
        }
        this.analytics.start(
          ENV.toolname,
          this.siteconfig.AnalyticsUrl,
          this.fastboot.isFastBoot,
          isTesting(),
          ENV.environment === 'development',
          this.logger,
        );
        this.opamps.requiresExternalSimulation = params.invokerId && params.invokerId !== '';
        this.opamps.load(); //Initiate it and let it float until we really need it
        return new IndexModel(this.opamps);
      });
  }

  afterModel() {
    const docService = getOwner(this).lookup('service:-document');
    if (docService) {
      docService.documentElement.setAttribute('lang', this.siteconfig.FullLocale);
    }
    set(this, 'headData.analyticsUrl', this.siteconfig.AnalyticsUrl);
    set(this, 'headData.contentLanguage', this.siteconfig.IsoLocale);
    set(this, 'headData.canonicalUrl', this.siteconfig.CanonicalUrl);
    set(this, 'headData.title', this.intl.t('page.title').toString());
    set(this, 'headData.description', this.intl.t('page.description').toString());
  }

  setupController(controller, model) {
    super.setupController(controller, model);
    if (!this.fastboot.isFastBoot) {
      controller.setupConnectionToChannel();
    }
    controller.setupAdvisors();
    controller.checkBrowserSuitability();
    controller.setupStudioLinks();
    controller.setupShoppingCartUrl();
    if (!this.fastboot.isFastBoot) {
      controller.loadFromUrlDesign();
    }
    loadTimeAnalytics(window.appStartDateTime, new Date().getTime(), this.analytics); // Analytics Placed here for Load Complete. (Unless there is a better place)
  }
}
